import { useCallback, useSyncExternalStore } from 'react'
import { pubSubGrid } from '../../state/pubSub'
import {
    isPreviewOnScrollTile,
    isScrollCursorTile,
    previewOnScrollChannel,
    scrollCursorChannel,
} from '../../state/state.grid'

const inactiveSnapshot = () => false

type TileChannel = (row: number, column: number) => string
type TilePredicate = (row: number, column: number) => boolean

const useGridTileChannelState = (
    channel: TileChannel,
    isTile: TilePredicate,
    row: number,
    column: number,
    isActive: boolean,
): boolean => {
    const isTargetable =
        isActive && typeof row === 'number' && typeof column === 'number'

    // inactive tiles don't subscribe at all — the channel is specific to
    // this tile's coordinate, so updates only ever notify the previously
    // and newly selected tiles
    const subscribe = useCallback(
        (callback: () => void) =>
            isTargetable
                ? pubSubGrid.on(channel(row, column), callback)
                : () => {},
        [isTargetable, row, column],
    )

    const getSnapshot = isTargetable
        ? () => isTile(row, column)
        : inactiveSnapshot

    return useSyncExternalStore(subscribe, getSnapshot, inactiveSnapshot)
}

// the tile whose preview plays once the scroll settles
export const useGridPreviewOnScrollTileState = (
    row: number,
    column: number,
    isActive: boolean,
): boolean =>
    useGridTileChannelState(
        previewOnScrollChannel,
        isPreviewOnScrollTile,
        row,
        column,
        isActive,
    )

// the tile the scroll cursor is sweeping over while the user scrolls
export const useGridScrollCursorTileState = (
    row: number,
    column: number,
    isActive: boolean,
): boolean =>
    useGridTileChannelState(
        scrollCursorChannel,
        isScrollCursorTile,
        row,
        column,
        isActive,
    )
